
import type { LineHistorySeries } from "@/components/odds/line-sparkline";

export interface TruncatedSeriesMeta {
  book: string;
  selection_key: string;
  earliest_returned_ts: string;
}

export interface OppositeMeta {
  book: string;
  selection_key: string;
  opposite_selection_key: string | null;
}

interface ExactPairResponseBody {
  data?: {
    series?: LineHistorySeries[];
    truncated?: boolean;
    truncated_series?: TruncatedSeriesMeta[];
    opposites?: OppositeMeta[];
  };
  error?: string;
}

export interface ExactPairSnapshot {
  series: LineHistorySeries[];
  truncated: boolean;
  truncatedSeries: TruncatedSeriesMeta[];
  opposites: OppositeMeta[];
  etag: string | null;
}

export type ExactPairResult =
  | { status: "ok"; snapshot: ExactPairSnapshot }
  | { status: "not-modified" }
  | { status: "unsupported" }
  | { status: "error"; message: string };

export async function fetchExactPairs(
  url: string,
  opts: { etag?: string | null; signal?: AbortSignal } = {},
): Promise<ExactPairResult> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.etag) {
    headers["If-None-Match"] = opts.etag;
  }
  let response: Response;
  try {
    response = await fetch(url, {
      headers,
      cache: "no-store",
      signal: opts.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { status: "error", message: "aborted" };
    }
    return { status: "error", message: "Unable to load chart history" };
  }

  if (response.status === 304) {
    return { status: "not-modified" };
  }
  if (
    response.status === 400 ||
    response.status === 404 ||
    response.status === 422
  ) {
    return { status: "unsupported" };
  }
  if (!response.ok) {
    return { status: "error", message: "Unable to load chart history" };
  }

  let body: ExactPairResponseBody;
  try {
    body = (await response.json()) as ExactPairResponseBody;
  } catch {
    return { status: "error", message: "Malformed chart history response" };
  }

  return {
    status: "ok",
    snapshot: {
      series: body.data?.series ?? [],
      truncated: Boolean(body.data?.truncated),
      truncatedSeries: body.data?.truncated_series ?? [],
      opposites: body.data?.opposites ?? [],
      etag: response.headers.get("etag"),
    },
  };
}

export interface OpenerQuote {
  line: number;
  price_american: number;
  price_decimal: number;
  observed_at: string;
}

export type EventOpeners = Record<string, Record<string, Record<string, OpenerQuote>>>;

const MAX_OPENER_EVENTS = 50;

export async function fetchOpeners(
  eventIds: readonly string[],
  signal?: AbortSignal,
): Promise<Record<string, EventOpeners>> {
  if (eventIds.length > MAX_OPENER_EVENTS) {
    throw new RangeError(`openers view accepts at most ${MAX_OPENER_EVENTS} event ids`);
  }
  const params = new URLSearchParams({ view: "openers", event_ids: eventIds.join(",") });
  const response = await fetch(`/api/linehistory?${params.toString()}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new Error(`openers view failed with ${response.status}`);
  }
  const body = (await response.json()) as { data: { events: Record<string, EventOpeners> } };
  return body.data.events;
}
